import {ReachSegment} from "../../apis/requests/templates/types";
import {SegmentCategory} from "../../apis/requests/segments/types";
import {SEGMENT_CATEGORIES} from "../segments/segment-form/constants";
import {compareFallbackLast} from "../segments/segmentCategoryBlocks";

const ALL_BUCKETS_VALUE_PREFIX = "all-buckets:";

export type SegmentTagValue = number | string;

export const allBucketsValue = (segmentCategory: SegmentCategory): string =>
  `${ALL_BUCKETS_VALUE_PREFIX}${segmentCategory}`;

export type SegmentCategoryTags = {
  segmentCategory: SegmentCategory;
  segments: ReachSegment[];
};

export type SegmentTagGroups = {
  exclusive: ReachSegment[];
  categories: SegmentCategoryTags[];
  general: ReachSegment[];
  validation: ReachSegment[];
};

const isValidationSegment = (segment: ReachSegment) => segment.type === "VALIDATION";

const isGeneralSegment = (segment: ReachSegment) => segment.type === "GENERAL";

export const groupSegmentTags = (segments: ReachSegment[], validationOnly: boolean): SegmentTagGroups => {
  const byPriority = [...segments].sort((a, b) => a.priority - b.priority || compareFallbackLast(a, b));
  if (validationOnly)
    return {exclusive: [], categories: [], general: [], validation: byPriority.filter(isValidationSegment)};
  const generalSegments = byPriority.filter(isGeneralSegment);
  return {
    exclusive: byPriority.filter((segment) => segment.type === "EXCLUSIVE"),
    categories: SEGMENT_CATEGORIES.map((segmentCategory) => ({
      segmentCategory,
      segments: generalSegments.filter((segment) => segment.segment_category === segmentCategory),
    })).filter((category) => category.segments.length > 0),
    general: generalSegments.filter((segment) => !segment.segment_category),
    validation: [],
  };
};

export const selectableSegments = (groups: SegmentTagGroups): ReachSegment[] => [
  ...groups.exclusive,
  ...groups.categories.flatMap((category) => category.segments),
  ...groups.general,
  ...groups.validation,
];

export const segmentIdsMatchingTemplateType = (
  segmentIds: number[],
  segments: ReachSegment[],
  isValidationTemplate: boolean
): number[] => {
  const segmentById = new Map(segments.map((segment) => [segment.id, segment]));
  return segmentIds.filter((id) => {
    const segment = segmentById.get(id);
    return !segment || isValidationSegment(segment) === isValidationTemplate;
  });
};

export const findSelectedExclusiveId = (selectedIds: number[], groups: SegmentTagGroups): number | undefined =>
  groups.exclusive.find((segment) => selectedIds.includes(segment.id))?.id;

const findAddedId = (previousIds: number[], requestedIds: number[], candidates: ReachSegment[]) =>
  candidates.find((segment) => requestedIds.includes(segment.id) && !previousIds.includes(segment.id))?.id;

const withoutAllBucketsCategories = (requested: SegmentTagValue[], groups: SegmentTagGroups): number[] => {
  const clearedBucketIds = new Set(
    groups.categories
      .filter((category) => requested.includes(allBucketsValue(category.segmentCategory)))
      .flatMap((category) => category.segments.map((segment) => segment.id))
  );
  return requested.filter((value): value is number => typeof value === "number" && !clearedBucketIds.has(value));
};

export const nextSegmentSelection = (
  previousIds: number[],
  requested: SegmentTagValue[],
  groups: SegmentTagGroups
): number[] => {
  const requestedIds = withoutAllBucketsCategories(requested, groups);
  const addedExclusiveId = findAddedId(previousIds, requestedIds, groups.exclusive);
  if (addedExclusiveId !== undefined) return [addedExclusiveId];
  return groups.categories.reduce((selectedIds, category) => {
    const addedBucketId = findAddedId(previousIds, selectedIds, category.segments);
    if (addedBucketId === undefined) return selectedIds;
    const otherBucketIds = new Set(category.segments.map((segment) => segment.id).filter((id) => id !== addedBucketId));
    return selectedIds.filter((id) => !otherBucketIds.has(id));
  }, requestedIds);
};
